<script lang="ts">
  import { onMount } from "svelte";
  import { fetchCurrentUser } from "../auth";
  import { desktopApiKeyFlag } from "../desktop-flag";
  import { appFetch, appFetchPOSTJson } from "../httputil";
  import LoginForm from "./LoginForm.svelte";

  type ListedKey = {
    id: string;
    prefix: string;
    createdAt: number;
    revoked: boolean;
  };

  let loggedOut = $state(false);
  let ready = $state(false);
  let creating = $state(false);
  let error = $state("");
  let token = $state("");
  let tokenKeyId = $state("");
  let tokenInput = $state<HTMLInputElement | null>(null);
  let keys = $state<ListedKey[]>([]);
  // Synchronous guard. A second click can run before the button re-renders disabled.
  const revokeInFlight = new Set<string>();
  let revokingIds = $state<string[]>([]);

  function stripNewParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("new")) {
      return;
    }
    url.searchParams.delete("new");
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  }

  function parseKeyList(body: unknown): ListedKey[] | null {
    if (!body || typeof body !== "object" || !Array.isArray((body as { keys?: unknown }).keys)) {
      return null;
    }
    const out: ListedKey[] = [];
    for (const item of (body as { keys: unknown[] }).keys) {
      if (!item || typeof item !== "object") {
        return null;
      }
      const row = item as { id?: unknown; prefix?: unknown; createdAt?: unknown; revoked?: unknown };
      if (typeof row.id !== "string" || typeof row.prefix !== "string") {
        return null;
      }
      out.push({
        id: row.id,
        prefix: row.prefix,
        createdAt: typeof row.createdAt === "number" ? row.createdAt : 0,
        revoked: row.revoked === true,
      });
    }
    return out;
  }

  async function errorMessage(rsp: Response): Promise<string> {
    try {
      const body = (await rsp.json()) as { error?: unknown };
      if (typeof body.error === "string" && body.error) {
        return body.error;
      }
    } catch {
      // non-JSON body
    }
    return `request failed: ${rsp.status}`;
  }

  async function fetchKeyList(): Promise<ListedKey[] | null> {
    let rsp: Response;
    try {
      rsp = await appFetch("/api/keys");
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      return null;
    }
    if (rsp.status !== 200) {
      error = await errorMessage(rsp);
      return null;
    }
    let body: unknown;
    try {
      body = await rsp.json();
    } catch {
      error = "failed to read API keys";
      return null;
    }
    const listed = parseKeyList(body);
    if (!listed) {
      error = "failed to read API keys";
      return null;
    }
    return listed;
  }

  async function createKey() {
    if (creating) {
      return;
    }
    creating = true;
    error = "";
    try {
      const rsp = await appFetch("/api/keys", { method: "POST" });
      if (rsp.status !== 200) {
        error = await errorMessage(rsp);
        // Too many keys: show the prefixes from a fresh list. Do not POST again.
        if (rsp.status === 400) {
          const createError = error;
          const listed = await fetchKeyList();
          error = createError;
          if (listed) {
            keys = listed;
            ready = true;
          }
        }
        return;
      }
      let body: unknown;
      try {
        body = await rsp.json();
      } catch {
        error = "failed to create api key";
        return;
      }
      const created = body as { token?: unknown; id?: unknown; prefix?: unknown; createdAt?: unknown };
      if (typeof created.token !== "string" || created.token === "") {
        error = "failed to create api key";
        return;
      }
      token = created.token;
      tokenKeyId = typeof created.id === "string" ? created.id : "";
      if (typeof created.id === "string" && typeof created.prefix === "string") {
        const row: ListedKey = {
          id: created.id,
          prefix: created.prefix,
          createdAt: typeof created.createdAt === "number" ? created.createdAt : 0,
          revoked: false,
        };
        if (!keys.some((k) => k.id === row.id)) {
          keys = [...keys, row];
        }
      }
      stripNewParam();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      creating = false;
    }
  }

  async function loadKeys() {
    const listed = await fetchKeyList();
    if (!listed) {
      return;
    }
    keys = listed;
    ready = true;
    const wantNew = new URL(window.location.href).searchParams.get("new") === "1";
    // A failed GET is not an empty account. Mint only when this 200 list is empty.
    if (listed.length > 0) {
      stripNewParam();
      return;
    }
    if (wantNew) {
      await createKey();
    }
  }

  async function revokeKey(id: string) {
    if (revokeInFlight.has(id) || !keys.some((k) => k.id === id)) {
      return;
    }
    revokeInFlight.add(id);
    revokingIds = [...revokeInFlight];
    error = "";
    try {
      const rsp = await appFetchPOSTJson("/api/keys/revoke", { id });
      if (!rsp.ok) {
        // The row was removed while this request was in flight. A 404 is not a new failure.
        if (rsp.status === 404 && !keys.some((k) => k.id === id)) {
          return;
        }
        error = await errorMessage(rsp);
        return;
      }
      keys = keys.filter((k) => k.id !== id);
      if (id === tokenKeyId) {
        token = "";
        tokenKeyId = "";
      }
    } catch (e) {
      if (!keys.some((k) => k.id === id)) {
        return;
      }
      error = e instanceof Error ? e.message : String(e);
    } finally {
      revokeInFlight.delete(id);
      revokingIds = [...revokeInFlight];
    }
  }

  async function copyToken() {
    try {
      await navigator.clipboard.writeText(token);
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  function formatCreatedAt(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) {
      return "";
    }
    return new Date(ms).toLocaleString();
  }

  onMount(async () => {
    let user;
    try {
      user = await fetchCurrentUser();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      return;
    }
    if (!user) {
      try {
        localStorage.setItem(desktopApiKeyFlag, String(Date.now()));
      } catch {
        // Blocked storage must not hide the login form.
      }
      loggedOut = true;
      return;
    }
    await loadKeys();
  });

  onMount(() => {
    const onPageHide = () => {
      if (tokenInput) {
        tokenInput.value = "";
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  });
</script>

<svelte:head>
  <meta name="robots" content="noindex" />
</svelte:head>

<main class="min-h-screen bg-[#f7f7f4] px-4 py-10 text-sm text-gray-900 dark:bg-gray-950 dark:text-gray-100">
  <div class="mx-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg bg-white p-6 dark:bg-gray-900">
    {#if loggedOut}
      <LoginForm />
    {:else}
      {#if error}
        <div class="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">{error}</div>
      {/if}

      {#if token}
        <input
          bind:this={tokenInput}
          readonly
          value={token}
          autocomplete="off"
          spellcheck="false"
          class="mb-3 w-full rounded border border-gray-300 px-2 py-2 font-mono dark:border-gray-700 dark:bg-gray-800"
        />
        <p class="mb-3">
          Paste this key into SumatraPDF. It is shown only this time. If you lose it, create another and revoke this one. Copy puts it on the clipboard, including clipboard history.
        </p>
        <button type="button" class="btn-outline-default mb-4" onclick={copyToken}>Copy</button>
      {/if}

      {#if ready}
        {#if keys.length > 0 && !token}
          <p class="mb-4">
            The old secret cannot be shown again. Press Create a key only for a computer that does not already have one, then revoke the one it replaced.
          </p>
        {/if}
        {#if keys.length > 0}
          <ul class="mb-4 grid gap-2">
            {#each keys as key (key.id)}
              <li class="flex items-center justify-between gap-3">
                <span class="min-w-0 break-all font-mono text-xs">{key.prefix}</span>
                {#if formatCreatedAt(key.createdAt)}
                  <span class="shrink-0 text-gray-500">{formatCreatedAt(key.createdAt)}</span>
                {/if}
                <button
                  type="button"
                  class="btn-outline-default shrink-0"
                  disabled={revokingIds.includes(key.id)}
                  onclick={() => revokeKey(key.id)}>Revoke</button
                >
              </li>
            {/each}
          </ul>
        {/if}
        <button type="button" class="btn-outline-default" disabled={creating} onclick={createKey}>Create a key</button>
      {/if}
    {/if}
  </div>
</main>
